import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AgeWarningModal from './AgeWarningModal';
import NotifyMeModal from './NotifyMeModal';
import { api } from '../config/api';
import { BookingContext } from '../contexts/BookingContext';

const MovieCard = ({ movie, showBookButton = true }) => {
  const navigate = useNavigate();
  const trailerLinkRef = useRef(null);
  const [showAgeWarning, setShowAgeWarning] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // BookingContext may not be wrapped in some legacy contexts; default
  // to null rather than throwing so the card stays usable.
  const bookingCtx = useContext(BookingContext) || null;

  const trailerUrl = movie.trailer || movie.trailerUrl || null;

  // Pre-fill data for the Notify Me modal — sourced from logged-in user
  const prefillEmail =
    (bookingCtx && bookingCtx.userDetails && bookingCtx.userDetails.email) || '';
  const prefillMobile =
    (bookingCtx &&
      bookingCtx.userDetails &&
      (bookingCtx.userDetails.mobile || bookingCtx.userDetails.phone)) ||
    '';
  const prefillUserId =
    (bookingCtx && bookingCtx.userDetails && bookingCtx.userDetails.id) || null;

  // Check if the visitor is already subscribed to this movie
  useEffect(() => {
    const status =
      (movie.status || '').toLowerCase() === 'coming soon' &&
      movie.releaseDate &&
      new Date(movie.releaseDate) > new Date();
    if (!status) {
      setIsSubscribed(false);
      return;
    }

    const hasIdentity =
      !!prefillUserId || !!prefillEmail || !!prefillMobile;
    if (!hasIdentity) {
      setIsSubscribed(false);
      return;
    }

    let cancelled = false;
    setCheckingStatus(true);
    api
      .getMovieNotificationStatus(movie.id, {
        email: prefillEmail,
        mobile: prefillMobile,
        userId: prefillUserId,
      })
      .then((res) => {
        if (!cancelled) setIsSubscribed(!!res.subscribed);
      })
      .catch(() => {
        if (!cancelled) setIsSubscribed(false);
      })
      .finally(() => {
        if (!cancelled) setCheckingStatus(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie.id, prefillEmail, prefillMobile, prefillUserId]);

  useEffect(() => {
    if (trailerUrl && trailerLinkRef.current && window.$ && window.$.fn.magnificPopup) {
      const $trailerLink = window.$(trailerLinkRef.current);
      $trailerLink.off('click.magnificPopup');
      $trailerLink.magnificPopup({
        type: 'iframe',
        mainClass: 'mfp-fade',
        removalDelay: 160,
        preloader: false,
        fixedContentPos: false,
        iframe: {
          patterns: {
            youtube: {
              index: 'youtube.com/',
              id: 'v=',
              src:
                'https://www.youtube.com/embed/%id%?autoplay=1&rel=0&modestbranding=1&showinfo=0&controls=1&fs=1&iv_load_policy=3&cc_load_policy=0&playsinline=1&origin=' +
                encodeURIComponent(window.location.origin) +
                '&enablejsapi=1',
            },
          },
        },
      });
      return () => {
        if ($trailerLink.length && $trailerLink.data('magnificPopup')) {
          try { $trailerLink.magnificPopup('close'); } catch (e) {}
        }
      };
    }
  }, [trailerUrl]);

  const handleBookNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const ageRating = movie.ageRating || movie.rating || '';
    if (ageRating.toUpperCase() === 'A') {
      setShowAgeWarning(true);
    } else {
      proceedToBooking();
    }
  };

  const proceedToBooking = () => {
    navigate(`/booking/${movie.id}`, { replace: false });
    window.scrollTo(0, 0);
  };

  const handleAgeWarningConfirm = () => { setShowAgeWarning(false); proceedToBooking(); };
  const handleAgeWarningClose = () => setShowAgeWarning(false);

  const getImageSrc = () => {
    const sources = [movie.image, movie.poster, movie.banner];
    for (const source of sources) {
      if (source && typeof source === 'string' && source.trim() !== '') return source;
    }
    return '/assets/img/movie-placeholder.png';
  };

  const handleCardClick = (e) => {
    // Prevent navigation if clicking on trailer button
    if (e.target.closest('.mc-play')) {
      return;
    }
    handleBookNow(e);
  };

  const todayIST = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  
  const movieReleaseDate =
    movie.releaseDate || movie.releasedate;
  
  // const releaseDateOnly = movieReleaseDate
  //   ? movieReleaseDate.split("T")[0]
  //   : null;
  const releaseDateOnly = movieReleaseDate
  ? new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(movieReleaseDate))
  : null;
  
  const isPreBooking =
    movie.status === "Now Showing" &&
    releaseDateOnly &&
    releaseDateOnly > todayIST;

  const isNewRelease =
  movie.status === "Now Showing" &&
    releaseDateOnly &&
    releaseDateOnly === todayIST;

  // Notify Me is available only for Coming Soon movies (where booking
  // is not yet open). Pre-booking movies show Book Now directly.
  const isNotYetBookable =
    movie.status === "Coming Soon" &&
    releaseDateOnly &&
    releaseDateOnly > todayIST;

  return (
    <>
      <div className="mc-card">
        {/* ── POSTER AREA ── */}
        <div className="mc-poster" onClick={handleCardClick}>
          <img
            src={getImageSrc()}
            alt={movie.title}
            className="mc-poster__img"
            onError={(e) => {
              if (!e.target.src.includes("movie-placeholder.png")) {
                e.target.src = "/assets/img/movie-placeholder.png";
              }
            }}
          />

          {/* Trailer play button */}
          {trailerUrl && (
            <a
              ref={trailerLinkRef}
              href={trailerUrl}
              className="mc-play popup-youtube"
              onClick={(e) => {
                if (
                  !trailerUrl.includes("youtube.com") &&
                  !trailerUrl.includes("youtu.be")
                ) {
                  e.preventDefault();
                }
              }}
              aria-label="Play Trailer"
            >
              <svg viewBox="0 0 24 24" fill="white" width="22" height="22">
                <path d="M8 5v14l11-7z" />
              </svg>
            </a>
          )}

          {isPreBooking && (
            <span className="mc-prebooking-badge">Pre Booking</span>
          )}

          {isNewRelease && (
            <span className="mc-newrelease-badge">New Release</span>
          )}

          {/* Book Now button - always visible */}
         
          {/* Hover overlay */}
          <div className="mc-overlay">
            <div className="mc-overlay__details">
              {movie.language && (
                <span>
                  <i className="fas fa-language"></i> {movie.language}
                </span>
              )}
              {movie.duration && (
                <span>
                  <i className="fas fa-clock"></i> {movie.duration}
                </span>
              )}
            </div>
            {showBookButton && (
            <button
              type="button"
              onClick={handleBookNow}
              className="mc-book-btn"
            >
              Book Now
            </button>
          )}

          {!showBookButton && isNotYetBookable && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowNotifyModal(true);
              }}
              className={isSubscribed ? 'mc-notify-btn mc-notify-btn-subscribed' : 'mc-notify-btn'}
              aria-label={
                isSubscribed
                  ? `Manage notifications for ${movie.title}`
                  : `Notify me when ${movie.title} is available`
              }
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6V11a6 6 0 1 0-12 0v5l-2 2v1h16v-1l-2-2z" />
              </svg>
              <span>{isSubscribed ? '✓ Subscribed' : 'Notify Me'}</span>
            </button>
          )}

          </div>
        </div>

        {/* ── META ── */}
        <div className="mc-meta">
          <p className="mc-title">
            {movie.title.replace(/\(.*?\)/g, "").trim()}
          </p>
          <div className="mc-tags">
            <div className="mc-primary-tags">
              {(movie.ageRating || movie.rating) && (
                <span className="mc-tag mc-tag--rating">
                  {movie.ageRating || movie.rating}
                </span>
              )}
              {movie.language && (
                <span className="mc-tag">{movie.language}</span>
              )}
            </div>

            {movie.genre && (
              <div className="mc-genre-row">
                {movie.genre
                  .replace(/[{}"]/g, "")
                  .split(",")
                  .map((g, index) => (
                    <span
                      key={index}
                      className={`mc-tag${
                        g.trim().toLowerCase() === "violence"
                          ? " mc-tag--danger"
                          : ""
                      }`}
                    >
                      {g.trim()}
                    </span>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AgeWarningModal
        isOpen={showAgeWarning}
        onClose={handleAgeWarningClose}
        onConfirm={handleAgeWarningConfirm}
      />

      <NotifyMeModal
        isOpen={showNotifyModal}
        onClose={() => {
          setShowNotifyModal(false);
          // Re-check status after modal closes so the button label updates
          api
            .getMovieNotificationStatus(movie.id, {
              email: prefillEmail,
              mobile: prefillMobile,
              userId: prefillUserId,
            })
            .then((res) => setIsSubscribed(!!res.subscribed))
            .catch(() => {});
        }}
        movie={movie}
        prefill={{
          email: prefillEmail,
          mobile: prefillMobile,
          userId: prefillUserId,
        }}
        api={api}
      />
    </>
  );
};

export default MovieCard;